import React, { useState } from "react"

import Card from "@mui/material/Card"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import { SENSITIVITY_OPTIONS } from "../../../../constants/sensitivity"

import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDTypography from "components/MDTypography"
import { useLoading } from "../../../LoadingContext"

export type SensitivitySettingEditFormData = {
  sensitivityEnabled: boolean
  sensitivity: string
  detectionEnabled: boolean
  detectionPeriodStart: string
  detectionPeriodEnd: string
  optionEnabled: boolean
  optionPeriodStart: string
  optionPeriodEnd: string
}

type SensitivitySettingUpdateProps = {
  title: string
  targetLabel?: string
  initialData: SensitivitySettingEditFormData
  submitLabel?: string
  onSubmit: (formData: SensitivitySettingEditFormData) => Promise<any>
  onClose: (updated: boolean) => void
}

const ManageSensitivitySettingUpdate: React.FC<SensitivitySettingUpdateProps> = ({
  title,
  targetLabel,
  initialData,
  submitLabel = "更新",
  onSubmit,
  onClose
}) => {
  const { startLoading, stopLoading } = useLoading()

  const [formData, setFormData] = useState<SensitivitySettingEditFormData>(initialData)
  const [errors, setErrors] = useState({
    sensitivity: "",
    detectionPeriod: "",
    optionPeriod: "",
    server: ""
  })

  const handleChange = (name: keyof SensitivitySettingEditFormData, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  const validate = () => {
    const validationErrors = {
      sensitivity: "",
      detectionPeriod: "",
      optionPeriod: "",
      server: ""
    }
    let hasError = false

    if (formData.sensitivityEnabled) {
      const sensitivity = Number(formData.sensitivity)
      if (formData.sensitivity === "" || Number.isNaN(sensitivity)) {
        validationErrors.sensitivity = "必須項目が入力されていません"
        hasError = true
      }
    }

    if (formData.detectionEnabled && (formData.detectionPeriodStart === "" || formData.detectionPeriodEnd === "")) {
      validationErrors.detectionPeriod = "必須項目が入力されていません"
      hasError = true
    }

    if (formData.optionEnabled && (formData.optionPeriodStart === "" || formData.optionPeriodEnd === "")) {
      validationErrors.optionPeriod = "必須項目が入力されていません"
      hasError = true
    }

    setErrors(validationErrors)
    return !hasError
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    try {
      startLoading()
      const response = await onSubmit(formData)

      if (response && (response.success === 1 || response.success === 2)) {
        onClose(true)
      } else if (response && response.success === 99) {
        setErrors((prev) => ({ ...prev, server: "セッションコードが無効です" }))
      } else {
        setErrors((prev) => ({
          ...prev,
          server: response?.message || "感度設定の更新に失敗しました"
        }))
      }
    } catch (error: any) {
      if (error.response?.data?.detail?.message) {
        setErrors((prev) => ({ ...prev, server: error.response.data.detail.message }))
      } else {
        setErrors((prev) => ({ ...prev, server: "サーバーへの接続に失敗しました" }))
      }
    } finally {
      stopLoading()
    }
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <button className="close-button" onClick={() => onClose(false)}>
          ×
        </button>

        <MDTypography variant="h4" fontWeight="medium">
          {title}
        </MDTypography>

        {targetLabel && (
          <MDTypography variant="body2" color="text" mt={1}>
            {targetLabel}
          </MDTypography>
        )}

        <MDBox style={{ height: "20px" }}></MDBox>

        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          <MDBox mt={1} mb={1}>
            <MDTypography variant="h6">感度設定</MDTypography>
            <MDTypography variant="caption" color="text">
              チェックを入れると入居者個別設定を使用します。チェックを外すとサービス提供元設定を継承します。
            </MDTypography>
          </MDBox>

          <MDBox mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  name="sensitivityEnabled"
                  checked={formData.sensitivityEnabled}
                  onChange={(e) => handleChange("sensitivityEnabled", e.target.checked)}
                />
              }
              label="感度設定を有効化"
            />
            <MDInput
              select
              SelectProps={{ native: true }}
              label="感度"
              name="sensitivity"
              value={formData.sensitivity}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange("sensitivity", e.target.value)}
              disabled={!formData.sensitivityEnabled}
              sx={{ width: "100%" }}
            >
              {SENSITIVITY_OPTIONS.map((option) => (
                <option key={option.value} value={String(option.value)}>
                  {option.label}
                </option>
              ))}
            </MDInput>
            {errors.sensitivity && (
              <MDBox color="#FF0000" pl={2} mb={2} fontSize="0.7em">
                {errors.sensitivity}
              </MDBox>
            )}
          </MDBox>

          <MDBox mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  name="detectionEnabled"
                  checked={formData.detectionEnabled}
                  onChange={(e) => handleChange("detectionEnabled", e.target.checked)}
                />
              }
              label="検知時間帯を有効化"
            />
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDInput
                type="time"
                name="detectionPeriodStart"
                value={formData.detectionPeriodStart}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange("detectionPeriodStart", e.target.value)
                }
                disabled={!formData.detectionEnabled}
              />
              〜
              <MDInput
                type="time"
                name="detectionPeriodEnd"
                value={formData.detectionPeriodEnd}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange("detectionPeriodEnd", e.target.value)
                }
                disabled={!formData.detectionEnabled}
              />
            </MDBox>
            {errors.detectionPeriod && (
              <MDBox color="#FF0000" pl={2} mb={2} fontSize="0.7em">
                {errors.detectionPeriod}
              </MDBox>
            )}
          </MDBox>

          <MDBox mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  name="optionEnabled"
                  checked={formData.optionEnabled}
                  onChange={(e) => handleChange("optionEnabled", e.target.checked)}
                />
              }
              label="検知時間帯（オプション）を有効化"
            />
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDInput
                type="time"
                name="optionPeriodStart"
                value={formData.optionPeriodStart}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange("optionPeriodStart", e.target.value)}
                disabled={!formData.optionEnabled}
              />
              〜
              <MDInput
                type="time"
                name="optionPeriodEnd"
                value={formData.optionPeriodEnd}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange("optionPeriodEnd", e.target.value)}
                disabled={!formData.optionEnabled}
              />
            </MDBox>
            {errors.optionPeriod && (
              <MDBox color="#FF0000" pl={2} mb={2} fontSize="0.7em">
                {errors.optionPeriod}
              </MDBox>
            )}
          </MDBox>

          {errors.server && (
            <MDBox color="#FF0000" pl={2} mb={2} mt={2} fontSize="0.7em">
              {errors.server}
            </MDBox>
          )}

          <MDBox mt={4} mb={2}>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              更新
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </div>
  )
}

export default ManageSensitivitySettingUpdate
